import {
  ChevronLeft,
  ChevronRight,
  Circle,
  Clipboard,
  Ellipsis,
  Logs,
  LucideEdit2,
  Mail,
  Signature,
  SortAsc,
  SquarePen,
  Users,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import SkeletonUserPage from "../../skeletons/UserSkeletons";
import { useFormatAmount } from "../../hooks/useFormatAmount";
import { useMutation, useQuery } from "react-query";
import { editAdminUser, getUser } from "../../sdk/users/users";
import { getLoanOfficerPerformance } from "../../sdk/loan-officer/loanofficer";
import { useError } from "../../context/ErrorProvider";
import { useState } from "react";
import EditUser from "../../components/edit-user/EditUser";
import EditUserSuccess from "../../components/edit-user/EditUserSuccess";
import { hasPermission } from "../../utils/permissions";
import useAuth from "../../hooks/useAuth";

const User = () => {
  const navigate = useNavigate();
  const formatAmount = useFormatAmount();
  const { id } = useParams();
  const { showError } = useError();
  const { auth } = useAuth();
  const [officerPerformance, setOfficerPerformance] = useState({});
  const [openEditUser, setOpenEditUser] = useState(false);
  const [openEditUserSuccess, setOpenEditUserSuccess] = useState(false);
  const [editUserDetails, setEditUserDetails] = useState({
    username: "",
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    role: "",
    branch: "",
    profileUrl: "",
  });

  const toggleEditUser = (user) => {
    setEditUserDetails({
      username: user?.username,
      firstName: user?.firstname,
      lastName: user?.lastname,
      email: user?.email,
      phoneNumber: user?.phone,
      role: user?.role_id,
      branch: user?.branch_id,
      profileUrl: user?.profile_image,
    });
    setOpenEditUser(true);
  };

  const closeEditUser = () => {
    setOpenEditUser(false);
  };

  const toggleEditUserSuccess = () => {
    setOpenEditUserSuccess(true);
  };

  const closeEditUserSuccess = () => {
    setOpenEditUserSuccess(false);
  };

  const {
    data: user,
    isLoading,
    refetch: refetchUser,
  } = useQuery({
    queryKey: ["user", id],
    queryFn: () => getUser(id),
    enabled: !!id,
  });

  useQuery({
    queryKey: ["perfomance", id],
    queryFn: async () => {
      const response = await getLoanOfficerPerformance(id);
      return response.data.data;
    },
    onSuccess: (data) => {
      setOfficerPerformance(data);
    },
    onError: (error) => {
      showError(error?.response?.data?.message || error.message);
    },
  });

  const { mutate: editAdminUserMutate, isLoading: editLoading } = useMutation({
    mutationKey: ["edit admin user", id],
    mutationFn: () =>
      editAdminUser(
        id,
        editUserDetails?.email,
        editUserDetails?.username,
        editUserDetails?.firstName,
        editUserDetails?.lastName,
        editUserDetails?.phoneNumber,
        editUserDetails?.role,
        editUserDetails?.branch,
        editUserDetails?.profileUrl,
      ),
    onSuccess: () => {
      setEditUserDetails({
        firstName: "",
        lastName: "",
        phoneNumber: "",
        email: "",
        profileUrl: "",
        branch: null,
        role: null,
      });
      refetchUser();
      closeEditUser();
      toggleEditUserSuccess();
    },
    onError: (error) => {
      showError(error?.response?.data?.message || error.message);
    },
  });

  const handleEditAdminUser = () => {
    editAdminUserMutate();
  };

  return (
    <>
      {openEditUserSuccess && (
        <EditUserSuccess closeEditUserSuccess={closeEditUserSuccess} />
      )}
      {openEditUser && (
        <EditUser
          closeAddUser={closeEditUser}
          handleCollectUserInformation={handleEditAdminUser}
          adminUser={editUserDetails}
          setAdminUser={setEditUserDetails}
          loading={editLoading}
        />
      )}
      {isLoading ? (
        <SkeletonUserPage />
      ) : (
        <div className="w-full">
          <div className="flex items-center gap-[5px] my-[10px]">
            <ChevronLeft color="#000000" size={17} />
            <p
              onClick={() => navigate("/dashboard/users")}
              className="text-[#222222] text-[16px] cursor-pointer font-bold"
            >
              Users
            </p>
            <ChevronRight color="#000000" size={17} />
            <p className="text-[#222222] capitalize text-[16px] font-bold">
              {user.firstname} {user.lastname}
            </p>
          </div>
          <div className="h-[145px] rounded-[10px] px-[10px] bg-[#0055A6] w-full py-[20px] pl-[10px] flex justify-between">
            <div className="flex gap-[20px]">
              <div className="h-[94px] w-[94px] rounded-[20px] overflow-hidden">
                {user?.profile_image ? (
                  <img
                    className="h-full w-full object-cover rounded-[20px]"
                    src={user?.profile_image}
                    alt="user"
                  />
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="94"
                    height="94"
                    viewBox="0 0 24 24"
                  >
                    <g
                      fill="none"
                      stroke="#ffffff"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                    >
                      <path d="M3 12a9 9 0 1 0 18 0a9 9 0 1 0-18 0" />
                      <path d="M9 10a3 3 0 1 0 6 0a3 3 0 1 0-6 0m-2.832 8.849A4 4 0 0 1 10 16h4a4 4 0 0 1 3.834 2.855" />
                    </g>
                  </svg>
                )}
              </div>
              <div className="h-full flex justify-around flex-col">
                <p className="text-white font-light text-[14px]">
                  {user.public_id}
                </p>
                <div className="flex items-center gap-[10px]">
                  <p className="font-medium capitalize text-[16px] text-white">
                    {user.firstname} {user.lastname}
                  </p>
                  <div className="flex items-center gap-[5px]">
                    <Circle size={17} color="#00A676" fill="#00A676" />
                    <p className="text-[#00A676] text-[14px] font-normal">
                      Active
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <p className="font-medium w-[140px] text-[14px] text-[#F3F3F3A6]">
                    Role
                  </p>
                  <p className="font-medium w-[140px] text-[14px] text-[#F3F3F3A6]">
                    Branch
                  </p>
                  <p className="font-medium w-[140px] text-[14px] text-[#F3F3F3A6]">
                    Phone
                  </p>
                </div>
                <div className="flex items-start">
                  <p className="font-medium w-[140px] text-[14px] text-[#F3F3F3]">
                    {user?.role?.name ?? "N/A"}
                  </p>
                  <p className="font-medium capitalize w-[140px] text-[14px] text-[#F3F3F3]">
                    {user?.branch?.name ?? "N/A"}
                  </p>
                  <p className="font-medium w-[140px] text-[14px] text-[#F3F3F3]">
                    {user?.phone ?? "N/A"}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-[20px]">
              <div className="w-[44px] h-[40px] border border-[#F3F3F3] rounded-[5px] flex items-center justify-center">
                <Ellipsis color="white" />
              </div>
              <a href={`mailto:${user.email}`}>
                <button className="h-[40px] bg-[#FFFFFF] px-[20px] rounded-[30px] text-[15px] text-[#000000] font-medium flex items-center justify-center gap-[10px]">
                  <Mail size={22} color="#000000" />
                  Send Email
                </button>
              </a>
            </div>
          </div>
          <div className="pl-[10px] w-full">
            <div className="flex my-[20px] justify-between">
              <div className="h-[600px] p-[20px] w-[49%] bg-[#ffffff] rounded-[10px] border border-[#A8A8A8]">
                <div className="flex items-center justify-between">
                  <p className="text-[#000000] font-semibold text-[20px]">
                    Loan Performance Overview
                  </p>
                  <button className="h-[35px] text-[#4D5874] text-[14px] font-medium bg-[#FFFFFF] px-[20px] border border-[#E0E3EB] rounded-[10px] flex items-center justify-center gap-[5px]">
                    <SortAsc />
                    Filter
                    <div className="h-[24px] w-[24px] text-[#F31616] text-[13px] font-medium rounded-full bg-[#FEECEC] flex items-center justify-center">
                      0
                    </div>
                  </button>
                </div>
                <div className="h-[30px] my-[30px] border-b-[1px] border-[#A8A8A8] bg-white flex items-center justify-between">
                  <p className="text-[#5C5C5C] text-[16px] font-normal">
                    Total Loans Managed
                  </p>
                  <p className="text-[#000000] font-bold text-[16px]">
                    {officerPerformance?.totalLoansManaged ?? 0}
                  </p>
                </div>
                <div className="h-[30px] my-[30px] border-b-[1px] border-[#A8A8A8] bg-white flex items-center justify-between">
                  <p className="text-[#5C5C5C] text-[16px] font-normal">
                    Active Loans
                  </p>
                  <p className="text-[#000000] font-bold text-[16px]">
                    {officerPerformance?.activeLoans ?? 0}
                  </p>
                </div>
                <div className="h-[30px] my-[30px] border-b-[1px] border-[#A8A8A8] bg-white flex items-center justify-between">
                  <p className="text-[#5C5C5C] text-[16px] font-normal">
                    Repaid Loans
                  </p>
                  <p className="text-[#000000] font-bold text-[16px]">
                    {officerPerformance?.repaidLoans ?? 0}
                  </p>
                </div>
                <div className="h-[30px] my-[30px] border-b-[1px] border-[#A8A8A8] bg-white flex items-center justify-between">
                  <p className="text-[#5C5C5C] text-[16px] font-normal">
                    Defaulted Loans
                  </p>
                  <p className="text-[#000000] font-bold text-[16px]">
                    {officerPerformance?.defaultedLoans ?? 0}
                  </p>
                </div>
                <div className="h-[30px] my-[30px] border-b-[1px] border-[#A8A8A8] bg-white flex items-center justify-between">
                  <p className="text-[#5C5C5C] text-[16px] font-normal">
                    Penalties Issued
                  </p>
                  <p className="text-[#000000] font-bold text-[16px]">
                    {officerPerformance?.penaltiesIssued ?? 0}
                  </p>
                </div>
                <div className="h-[30px] my-[30px] border-b-[1px] border-[#A8A8A8] bg-white flex items-center justify-between">
                  <p className="text-[#5C5C5C] text-[16px] font-normal">
                    Total Loan Amount Issued
                  </p>
                  <p className="text-[#000000] font-bold text-[16px]">
                    {formatAmount(
                      officerPerformance?.totalLoanAmountIssued ?? 0,
                    )}
                  </p>
                </div>
                <div className="h-[30px] my-[30px] border-b-[1px] border-[#A8A8A8] bg-white flex items-center justify-between">
                  <p className="text-[#5C5C5C] text-[16px] font-normal">
                    Recovered Amount
                  </p>
                  <p className="text-[#000000] font-bold text-[16px]">
                    {formatAmount(officerPerformance?.recoveredAmount ?? 0)}
                  </p>
                </div>
                <div className="h-[30px] my-[30px] border-b-[1px] border-[#A8A8A8] bg-white flex items-center justify-between">
                  <p className="text-[#5C5C5C] text-[16px] font-normal">
                    Outstanding Amount
                  </p>
                  <p className="text-[#000000] font-bold text-[16px]">
                    {formatAmount(officerPerformance?.outstandingAmount ?? 0)}
                  </p>
                </div>
              </div>
              <div className="h-[600px] p-[20px] w-[49%] bg-[#ffffff] rounded-[10px] border border-[#A8A8A8]">
                <p className="text-[#000000] text-[20px] font-semibold">
                  Quick Actions
                </p>
                <div className="h-[70px] cursor-pointer w-full my-[15px] border-[0.7px] border-[#000000] bg-[#ffffff] flex items-center justify-between rounded-[5px]">
                  <div className="w-[10%] flex items-center justify-center h-full">
                    <Signature size={18} color="#000000" />
                  </div>
                  <div className="w-[85%] flex flex-col justify-center h-full">
                    <p className="text-[#000000] font-medium text-[14px]">
                      View All Loans by Officer
                    </p>
                    <p className="text-[#737373] text-[12px] font-normal">
                      See the full list of loans handled by this officer.
                    </p>
                  </div>
                  <div className="w-[5%] flex items-center justify-center h-full">
                    <ChevronRight color="#000000" size={24} />
                  </div>
                </div>
                <div className="h-[70px] cursor-pointer w-full my-[15px] border-[0.7px] border-[#000000] bg-[#ffffff] flex items-center justify-between rounded-[5px]">
                  <div className="w-[10%] flex items-center justify-center h-full">
                    <Clipboard size={18} color="#000000" />
                  </div>
                  <div className="w-[85%] flex flex-col justify-center h-full">
                    <p className="text-[#000000] font-medium text-[14px]">
                      Generate Loan Officer Report
                    </p>
                    <p className="text-[#737373] text-[12px] font-normal">
                      Download performance and loan summaries in one click.
                    </p>
                  </div>
                  <div className="w-[5%] flex items-center justify-center h-full">
                    <ChevronRight color="#000000" size={24} />
                  </div>
                </div>
                <div
                  onClick={() => toggleEditUser(user)}
                  className="h-[70px] cursor-pointer w-full my-[15px] border-[0.7px] border-[#000000] bg-[#ffffff] flex items-center justify-between rounded-[5px]"
                >
                  <div className="w-[10%] flex items-center justify-center h-full">
                    <SquarePen size={18} color="#000000" />
                  </div>
                  <div className="w-[85%] cursor-pointer flex flex-col justify-center h-full">
                    <p className="text-[#000000] font-medium text-[14px]">
                      Edit User Details
                    </p>
                    <p className="text-[#737373] text-[12px] font-normal">
                      Update profile or assignment information.
                    </p>
                  </div>
                  <div className="w-[5%] flex items-center justify-center h-full">
                    <ChevronRight color="#000000" size={24} />
                  </div>
                </div>
                {hasPermission(
                  auth?.roles,
                  "44ba3dcf-1c12-8c58-90e4-8da4659336a1",
                ) && (
                  <div
                    onClick={() =>
                      navigate(
                        `/dashboard/users/${user?.id}/edit-user-permissions`,
                      )
                    }
                    className="h-[70px] cursor-pointer w-full my-[15px] border-[0.7px] border-[#000000] bg-[#ffffff] flex items-center justify-between rounded-[5px]"
                  >
                    <div className="w-[10%] flex items-center justify-center h-full">
                      <LucideEdit2 size={18} color="#000000" />
                    </div>
                    <div className="w-[85%] cursor-pointer flex flex-col justify-center h-full">
                      <p className="text-[#000000] font-medium text-[14px]">
                        Edit User Permissions
                      </p>
                      <p className="text-[#737373] text-[12px] font-normal">
                        Update role or permissions information.
                      </p>
                    </div>
                    <div className="w-[5%] flex items-center justify-center h-full">
                      <ChevronRight color="#000000" size={24} />
                    </div>
                  </div>
                )}
                <div className="h-[70px] cursor-pointer w-full my-[15px] border-[0.7px] border-[#000000] bg-[#ffffff] flex items-center justify-between rounded-[5px]">
                  <div className="w-[10%] flex items-center justify-center h-full">
                    <Users size={18} color="#000000" />
                  </div>
                  <div className="w-[85%] flex flex-col justify-center h-full">
                    <p className="text-[#000000] font-medium text-[14px]">
                      View Assigned Clients
                    </p>
                    <p className="text-[#737373] text-[12px] font-normal">
                      Browse all borrowers linked to this officer.
                    </p>
                  </div>
                  <div className="w-[5%] flex items-center justify-center h-full">
                    <ChevronRight color="#000000" size={24} />
                  </div>
                </div>
                <div
                  onClick={() =>
                    navigate(`/dashboard/users/${user?.id}/audit-trail`)
                  }
                  className="h-[70px] cursor-pointer w-full border-[0.7px] border-[#000000] bg-[#ffffff] flex items-center justify-between rounded-[5px]"
                >
                  <div className="w-[10%] flex items-center justify-center h-full">
                    <Logs size={18} color="#000000" />
                  </div>
                  <div className="w-[85%] flex flex-col justify-center h-full">
                    <p className="text-[#000000] font-medium text-[14px]">
                      Audit Trail
                    </p>
                    <p className="text-[#737373] text-[12px] font-normal">
                      View a detailed log of all actions and activities
                      performed by this user.
                    </p>
                  </div>
                  <div className="w-[5%] flex items-center justify-center h-full">
                    <ChevronRight color="#000000" size={24} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default User;
